import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { MapsMarkers, Markers } from '@base/models/responses/maps-marker.res';
import { ModalService } from '@base/services/private/modal.service';
import { ModalHeaderComponent } from '@base/components/private/modals/modal-header/modal-header.component';
import { ModalBodyComponent } from '@base/components/private/modals/modal-body/modal-body.component';
import { SkeletonProductListComponent } from '@base/components/private/skeleton/skeleton-product-list/skeleton-product-list.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { TranslatePipe } from '@base/pipes/private/translate.pipe';
import { MeetService } from '@base/services/private/meet.service';
import { GlobalStateService } from '@base/services/private/global-state.service';
import { GlobalService } from '@base/services/private/global.service';
import { Floorplan } from '@base/models/responses/floorplan.res';
import { Subscription } from 'rxjs';
import { Entity } from '@base/enums/menu-config.enum';
import { FloorplanAction } from '@base/enums/floorplan.enum';
import { DynamicImportLoaderService } from '@base/services/private/dynamic-import.service';
import { WhiteLabel } from '@config/whitelabel-keys.constant';
import { LoadMFESrvAbstract } from '@base/abstracts/services/load-mfe.abstract';

@Component({
  selector: 'app-modal-marker-list',
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    SkeletonProductListComponent,
    NgxSkeletonLoaderComponent,
    forwardRef(() => TranslatePipe),
  ],
  templateUrl: './modal-marker-list.component.html',
  styleUrl: './modal-marker-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalMarkerListComponent implements OnInit, OnDestroy {
  private readonly modalSrv = inject(ModalService);
  private readonly meetSrv = inject(MeetService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly globalStateSrv = inject(GlobalStateService);
  private readonly globalSrv = inject(GlobalService);
  private readonly dynamicImportLoaderSrv = inject(DynamicImportLoaderService);
  private loadMfeSrv: LoadMFESrvAbstract | null = null;

  @Input() modalId = '';
  @Input() exhibitorId = '';
  @Input() isShowPlaceholder = true;
  @Input() markerList: MapsMarkers = {
    data: [],
  };
  @Input() isOpenDirectly = true; // if auto open modal without trigger card-exhibitor
  markerDetail$: Subscription | null = null;
  floorplanMarker: Floorplan | null = null;
  private loadMfePromise: Promise<void> | null = null;

  ngOnInit(): void {
    this.getMarkerDetail();
    this.loadMfePromise = this.loadInformaMfeService();
    void this.initializeMarkerList();
  }

  private async initializeMarkerList(): Promise<void> {
    await this.loadMfePromise;
    if (this.isOpenDirectly) {
      this.getMarkerList();
    } else if (this.markerList?.data?.length === 0) {
      this.close();
    }
  }

  ngOnDestroy(): void {
    this.globalSrv.destroyModalRef(this.modalId);
    this.markerDetail$?.unsubscribe();
  }

  /**
   * Lazily loads the Informa MFE service and caches it for floorplan gate checks.
   * @returns Promise<void>
   */
  private async loadInformaMfeService(): Promise<void> {
    try {
      this.loadMfeSrv = await this.dynamicImportLoaderSrv.loadService<LoadMFESrvAbstract>(WhiteLabel.SERVICES.LoadMfe);
    } catch (error) {
      console.error('Failed to load Informa MFE service:', error);
    }
  }

  /**
   * Get realtime marker detail
   */
  getMarkerDetail(): void {
    this.markerDetail$ = this.globalStateSrv.getMarkerDetail().subscribe({
      next: (data) => {
        this.floorplanMarker = data;
      },
    });
  }

  /**
   * Retrieves the list of markers for the exhibitor from the MeetService.
   * Automatically selects the marker if only one is available.
   * Updates the UI state after data is loaded.
   * @returns void
   */
  getMarkerList(): void {
    this.meetSrv.getMapsMarker(this.exhibitorId).subscribe({
      next: (res) => {
        this.markerList = res;

        if (this.markerList.data.length > 0 && this.markerList.data.length < 2) {
          const firstBooth = this.markerList.data[0];
          this.selectMarker(firstBooth);
        }
        this.isShowPlaceholder = false;
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.markForCheck();
      },
      error: (err) => {
        console.error('Error fetching marker list:', err);
        this.isShowPlaceholder = false;
      },
    });
  }

  /**
   * Closes the modal with the given modalId.
   * @returns void
   */
  close(): void {
    this.modalSrv.close(this.modalId);
  }

  /**
   * Selects a marker and closes the modal with floorplan details.
   * @param item - The marker selected by the user.
   * @returns void
   */
  selectMarker(item: Markers) {
    const pgId = this.globalStateSrv.getState()?.loader?.pg_id ?? '';
    if (this.loadMfeSrv?.canUseExternalFloorplan(pgId, this.globalStateSrv.isAnon())) {
      this.close();
      this.modalSrv.showModalExternalFloorplan({
        addsData: { floorplanId: item.map_id ?? null, markerId: item.marker_id ?? null },
      });
      return;
    }
    this.close();

    // trigger marker detail from service to init render maps iframe
    const currentFloorplanId = this.floorplanMarker?.floorplan_id;
    if (!this.floorplanMarker?.marker_id || item.map_id !== currentFloorplanId) {
      const data: Floorplan = {
        floorplan_id: item.map_id,
        location_name: item.identifier!,
        marker_id: item.marker_id,
        action: FloorplanAction.LOCATE_CLICK,
        source: Entity.COMPANIES,
        type: Entity.COMPANIES,
      };
      this.globalStateSrv.setMarkerDetail(data);
    } else {
      // once we open the correct url, we can use post message
      this.globalStateSrv.postMessageFloorplan(item.marker_id, 'company');
    }
  }
}
